/**
 * View Info identify chrome: hand cursor + selected-feature paint.
 * Shared by MapLibre expressions and OpenLayers style functions.
 */
import { tokens } from '@/design';
import {
  featureMatchesFilter,
  vectorFilterToMaplibre,
  type VectorFilter,
} from './vectorStyleModel';

export const VECTOR_IDENTIFY_CURSOR = 'grab';

export const VECTOR_IDENTIFY_HIGHLIGHT = {
  fillColor: tokens.color.semantic.warning,
  fillOpacity: 0.55,
  lineColor: tokens.color.semantic.danger,
  lineWidth: 4,
  pointRadius: 8,
} as const;

export type VectorHighlightTarget =
  | string
  | number
  | null
  | ReadonlyArray<string | number>
  | VectorFilter;

const ID_GET_KEYS = [
  'OBJECTID_1',
  'OBJECTID',
  'objectid',
  'FID',
  'fid',
  'gid',
  'id',
  'ID',
] as const;

export function resolveVectorHighlightTarget(options: {
  readonly identifyArmed: boolean;
  readonly identifyFeatureId?: string | number | null;
  readonly tableSelectedFeatureIds?: ReadonlyArray<string | number>;
  readonly filter: VectorFilter;
}): VectorHighlightTarget {
  if (options.identifyArmed) return options.identifyFeatureId ?? null;
  if (options.tableSelectedFeatureIds?.length) return options.tableSelectedFeatureIds;
  if (options.filter.attribute) return options.filter;
  return null;
}

export function isHighlightActive(target: VectorHighlightTarget): boolean {
  if (target == null || target === '') return false;
  if (Array.isArray(target)) return target.length > 0;
  if (isVectorFilter(target)) return Boolean(target.attribute);
  return true;
}

export function maplibreSelectedPaint(
  selectedId: string | number,
  whenSelected: string | number,
  otherwise: string | number,
): unknown[] {
  return maplibreHighlightPaint(selectedId, whenSelected, otherwise) as unknown[];
}

export function maplibreHighlightPaint(
  target: VectorHighlightTarget,
  whenSelected: string | number,
  otherwise: string | number,
): unknown[] | string | number {
  const condition = maplibreHighlightCondition(target);
  if (!condition) return otherwise;
  return ['case', condition, whenSelected, otherwise];
}

export function maplibreHighlightCondition(target: VectorHighlightTarget): unknown[] | null {
  if (!isHighlightActive(target) || target == null) return null;
  if (isVectorFilter(target)) {
    const expression = vectorFilterToMaplibre(target);
    return expression ? [...expression] : null;
  }
  const ids = Array.isArray(target) ? target : [target];
  const clauses = ids.flatMap((id) => idMatchClauses(id));
  if (clauses.length === 0) return null;
  return clauses.length === 1 ? clauses[0] : ['any', ...clauses];
}

export function propertiesMatchHighlight(
  properties: Readonly<Record<string, unknown>>,
  featureId: string | number | null | undefined,
  target: VectorHighlightTarget,
): boolean {
  if (!isHighlightActive(target) || target == null) return false;
  if (isVectorFilter(target)) {
    return featureMatchesFilter(properties, target);
  }
  const ids = Array.isArray(target) ? target : [target];
  const candidates = [
    featureId,
    ...ID_GET_KEYS.map((key) => properties[key]),
  ];
  return ids.some((wanted) =>
    candidates.some((value) => value != null && String(value) === String(wanted)),
  );
}

function isVectorFilter(target: VectorHighlightTarget): target is VectorFilter {
  return (
    typeof target === 'object'
    && target !== null
    && !Array.isArray(target)
    && 'attribute' in target
  );
}

function idMatchClauses(selectedId: string | number): unknown[][] {
  const wanted = String(selectedId);
  return [
    ['==', ['to-string', ['id']], wanted],
    ...ID_GET_KEYS.map((key) => ['==', ['to-string', ['get', key]], wanted]),
  ];
}

export function applyIdentifyCursor(
  elements: ReadonlyArray<HTMLElement | null | undefined>,
  armed: boolean,
): void {
  const cursor = armed ? VECTOR_IDENTIFY_CURSOR : '';
  for (const element of elements) {
    if (!element) continue;
    element.style.cursor = cursor;
  }
}
